import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useEffectOnce, useEvent } from 'react-use';
import { HistoryService } from '@rocheglobal/component-library/src/services/history';
import { AnyProps } from '../types';
import { MedicineProductsService } from '../../services/medicineProducts';
import { MedicineProduct, Product, SearchParams } from '../../services/medicineProducts/types';

const getProductInformationBySapCode = (sapCode, links) => {
  try {
    const entity = links.find((item) => item.name === sapCode);
    return entity.content.links.items?.map((link) => ({
      href: link.url || '#',
      link: link.text || '',
    }));
  } catch {
    return [];
  }
};

const getEducationMaterial = (sapCode, links) => {
  try {
    const matchingLink = links.find((item) => item.name === sapCode);
    if (matchingLink
        && matchingLink.content
        && matchingLink.content.training_material_url
    ) {
      return matchingLink.content.training_material_url.url;
    }
    return '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error occurred while retrieving education material: ${error}`);
    return '';
  }
};

const getInformationByType = (product: MedicineProduct, infoTypeCode: string) => (
  product.documents?.filter((doc) => doc.infoTypeCode === infoTypeCode)
    .map((doc) => ({ href: doc.downloadUrl, link: doc.strengthName }))
);

export const AlphabeticListing = ({ slot }: AnyProps): JSX.Element => {
  const { storyblok } = useStaticQuery(graphql`
    {
        storyblok {
          pharmaLinks: PharmamarketinglinksItems(per_page: 30) {
            items {
              name
              content {
                training_material_url {
                  url
                }
                links
              }
            }
          }
        }
    }
  `);
  const pharmaLinks = storyblok.pharmaLinks?.items;
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [isFetchingProducts, setIsFetchingProducts] = useState(true);

  const getStateFromURL = (): SearchParams => {
    const url = HistoryService.getActiveUrl();
    return {
      search: url.searchParams.get('search') || '',
    };
  };

  const updateStateFromURL = () => {
    const { search: searchParam } = getStateFromURL();
    setSearch(searchParam);
  };

  const handleSearch = useCallback(({ detail }) => {
    setSearch(detail);
  }, []);

  useEffectOnce(updateStateFromURL);
  useEffectOnce(() => {
    MedicineProductsService.getMedicineProducts().then((medicineProducts: MedicineProduct[]) => {
      const allProducts = medicineProducts
        .map((product) => ({
          uid: product.sapCode,
          primaryName: product.familyName,
          secondaryName: product.activeSubstance,
          blackTriangle: product.blackTriangle,
          educationMaterial: getEducationMaterial(product.sapCode, pharmaLinks),
          redHandLetter: false,
          usageInformation: getInformationByType(product, 'PIL'),
          specialInformation: getInformationByType(product, 'SPC'),
          productInformation: getProductInformationBySapCode(product.sapCode, pharmaLinks),
        }));
      setProducts(allProducts);
      setIsFetchingProducts(false);
    });
  });

  useEffect(() => {
    const url = HistoryService.getActiveUrl();
    const isEmpty = search.length <= 0;

    if (isEmpty) {
      url.searchParams.delete('search');
    } else {
      url.searchParams.set('search', search);
    }

    window.history.pushState(null, '', url.toString());
  }, [search]);

  useEvent('rocheAlphabeticListingSearchValueChange', handleSearch);

  const filteredProducts = useMemo(() => (
    JSON.stringify(products.filter((item) => item.primaryName.toLowerCase().includes(search)))
  ), [products, search]);

  if (isFetchingProducts) {
    return <roche-loading-spinner slot={slot} style={{ marginTop: '3rem' }}/>;
  }

  return (
    filteredProducts && <roche-alphabetic-listing
      slot={slot}
      header-col-1='Name'
      header-col-2= 'Gebrauchsinformation'
      header-col-3= 'Fachinformation'
      header-col-4= 'Produktinformation'
      value={search}
      products={filteredProducts}
    />
  );
};
